import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiShield, FiCheckCircle, FiHelpCircle, FiChevronDown, FiArrowRight } = FiIcons;

const CertificationSection = ({ nextStep, t }) => {
  const [activeAccordion, setActiveAccordion] = useState(null);

  const certificationBodies = [
    {
      name: 'GIA',
      fullName: 'Gemological Institute of America',
      description: 'The most recognized and trusted diamond grading laboratory worldwide.',
      features: ['4Cs Grading', 'Laser Inscription', 'Digital Report', 'Global Recognition']
    },
    {
      name: 'AGS',
      fullName: 'American Gem Society',
      description: 'Known for their scientific approach and precision in diamond grading.',
      features: ['Cut Grading Excellence', 'Light Performance', 'Ideal Cut Standards', 'Scientific Methods']
    },
    {
      name: 'SSEF',
      fullName: 'Swiss Gemmological Institute',
      description: 'European leader in gemstone certification and research.',
      features: ['European Standards', 'Research Excellence', 'Colored Diamonds', 'Natural vs Treated']
    },
    {
      name: 'Sarine',
      fullName: 'Sarine Light',
      description: 'Advanced light performance analysis and diamond technology solutions.',
      features: ['Light Performance Mapping', 'AI Technology', 'Diamond Journey Tracking', 'Light Behavior Analysis']
    }
  ];

  const faqItems = [
    {
      question: "What is diamond certification?",
      answer: "Diamond certification is an independent evaluation of a diamond's characteristics by a qualified gemological laboratory. It provides an unbiased assessment of the diamond's quality based on the 4Cs."
    },
    {
      question: "Why is certification important?",
      answer: "Certification ensures you know exactly what you're buying. It provides confidence in the diamond's quality, helps determine fair pricing, and serves as proof for insurance purposes."
    },
    {
      question: "Can certification be faked?",
      answer: "Reputable laboratories use advanced security features like laser inscriptions, unique report numbers, and online verification systems to prevent counterfeiting."
    },
    {
      question: "How long does certification take?",
      answer: "The certification process typically takes 2-4 weeks, depending on the laboratory and the complexity of the evaluation required."
    },
    {
      question: "Does certification affect diamond value?",
      answer: "Yes, certification from reputable laboratories can increase a diamond's value by providing buyer confidence and enabling accurate pricing based on verified quality."
    }
  ];

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-green-400 to-pantone-blue bg-clip-text text-transparent">
            {t('certificationTitle')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('certificationDescription')}
          </p>
        </motion.div>

        {/* Certificate Visualization */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-16"
        >
          <div className="relative max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <div className="bg-gradient-to-r from-green-500 to-pantone-blue p-4 rounded-xl mr-4">
                    <SafeIcon icon={FiAward} className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{t('gradingReport')}</h3>
                    <p className="text-gray-400">{t('certificateNumber')}: DG2024001234</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="bg-green-500/20 border border-green-400 px-4 py-2 rounded-lg">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-400 inline mr-2" />
                    <span className="text-green-300 font-semibold">{t('verified')}</span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-semibold text-white mb-4">{t('diamondDetails')}</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('shape')}:</span>
                      <span className="text-white font-semibold">Round Brilliant</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('caratWeight')}:</span>
                      <span className="text-white font-semibold">1.50 ct</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('colorGrade')}:</span>
                      <span className="text-white font-semibold">G</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('clarityGrade')}:</span>
                      <span className="text-white font-semibold">VS1</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('cutGrade')}:</span>
                      <span className="text-white font-semibold">Excellent</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-white mb-4">{t('measurements')}</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('dimensions')}:</span>
                      <span className="text-white font-semibold">7.42 x 7.45 x 4.48 mm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('table')}:</span>
                      <span className="text-white font-semibold">57%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('depth')}:</span>
                      <span className="text-white font-semibold">60.3%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('girdle')}:</span>
                      <span className="text-white font-semibold">Medium to Slightly Thick</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">{t('fluorescence')}:</span>
                      <span className="text-white font-semibold">None</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="flex items-center justify-center">
                  <SafeIcon icon={FiShield} className="w-6 h-6 text-pantone-blue/80 mr-3" />
                  <span className="text-gray-300">{t('certificateGuarantee')}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Certification Bodies */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-center text-white mb-8">{t('trustedBodies')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certificationBodies.map((body, index) => (
              <motion.div
                key={body.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-pantone-blue/10 transition-all duration-300"
              >
                <div className="text-center mb-4">
                  <div className="bg-gradient-to-r from-pantone-blue to-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white font-bold text-xl">{body.name}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white">{body.fullName}</h3>
                </div>
                
                <p className="text-gray-300 text-center mb-4 text-sm">{body.description}</p>
                
                <div className="space-y-2">
                  {body.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-white mb-8">{t('faq')}</h2>
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-white/5 transition-all duration-300"
                  >
                    <div className="flex items-center">
                      <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-pantone-blue/80 mr-3" />
                      <span className="text-lg font-semibold text-white">{item.question}</span>
                    </div>
                    <motion.div
                      animate={{ rotate: activeAccordion === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <SafeIcon icon={FiChevronDown} className="w-5 h-5 text-gray-400" />
                    </motion.div>
                  </button>
                  
                  <AnimatePresence>
                    {activeAccordion === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-white/10"
                      >
                        <div className="p-6 pt-4">
                          <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Continue Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <button
            onClick={nextStep}
            className="group bg-gradient-to-r from-green-600 to-pantone-blue hover:from-pantone-blue hover:to-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl transition-all duration-300 flex items-center gap-3 mx-auto"
          >
            {t('nextJourney')}
            <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default CertificationSection;